/* Live CodeBaby / Bridge3 adapter.
 *
 * Mounts a real avatar by injecting the loader script (URL provided by
 * Bridge3 when the studio provisioned the avatar — see
 * studio/cli/provision.ts and out/<slug>/src/constants/avatars.ts) and
 * driving it through the `na-*` jQuery event bus documented in
 * docs/AvatarEventsReference.md.
 *
 * The patterns required by CODEBABY_GUIDE.md (§1–§5, §12) are load-
 * bearing here — re-read the guide before changing them:
 *
 *   - Trigger outbound events on $(document) only (never $(window)).
 *   - Listen for both `eventName.vidbaby` and `eventName` (different
 *     loader builds emit one or the other).
 *   - Namespace all our jQuery handlers under `.cbBridge` so cleanup is
 *     a single `.off('.cbBridge')`.
 *   - 10-second playerBuilt watchdog with a specific error so the
 *     failure mode is obvious in the console.
 *   - On mic-final, update display only — do NOT call ask(). The loader
 *     already auto-asks the final transcript (the "auto-ask trap").
 *   - startData stays ≤1-2 KB. Dynamic context rides
 *     `na-conversationalContextData` post-mount.
 *   - On unmount: remove the script tag, detach `.cbBridge` handlers,
 *     clear timers, wipe container innerHTML. Do NOT delete
 *     window.vidbaby / window.jQuery — other mounts in the same page
 *     session depend on them.
 */

import type { AvatarSession, MountOptions, MountResult, ResponsePayload } from './types';

interface VidbabyGlobal {
  $: JQueryLike;
  /** Runtime loader config. The integration can set fields here after init
   *  and the loader reads them live (no refresh). We use it to override the
   *  OpenAI Realtime semantic-VAD eagerness — 'low' makes the mic less
   *  trigger-happy, which suppresses phantom turns from coughs/throat-clears
   *  (the Whisper-hears-"hello" problem) at the source. */
  config?: { realtimeVadEagerness?: VadEagerness; [k: string]: unknown };
}

/** OpenAI Realtime semantic-VAD eagerness. 'low' = wait for clearer speech
 *  (fewer false triggers); 'high' = fire eagerly. Per the loader, settable on
 *  vidbaby.config at runtime, not persisted server-side. */
export type VadEagerness = 'low' | 'medium' | 'high' | 'auto';

interface JQueryLike {
  (target: Document | string): JQueryWrapped;
}

interface JQueryWrapped {
  on(event: string, handler: (...args: unknown[]) => void): JQueryWrapped;
  off(event: string): JQueryWrapped;
  trigger(event: string, payload?: unknown): JQueryWrapped;
}

declare global {
  interface Window {
    vidbaby?: VidbabyGlobal;
    jQuery?: JQueryLike;
    $?: JQueryLike;
  }
}

/** Pulled from the avatar.codebaby.com loader script — kept as a
 *  fallback for the loader URL when the studio hasn't written an
 *  explicit per-persona URL yet. */
const DEFAULT_LOADER_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BRIDGE3_LOADER_URL) ||
  'https://avatar.codebaby.com/loader.js';

const MOUNT_WATCHDOG_MS = 10_000;
/** jQuery namespace used to scope OUR listeners for `.off()` cleanup —
 *  one `.off('.cbBridge')` at unmount detaches everything we bound. */
const HANDLER_NAMESPACE = '.cbBridge';
/** The CodeBaby loader binds and triggers events under `.vidbaby`. jQuery
 *  filters namespaced triggers against listener namespaces, so to hear the
 *  loader's outbound events AND to be heard by its listeners, ours must
 *  share the `.vidbaby` namespace. Listeners get `${event}.vidbaby.cbBridge`
 *  (matches loader's `.vidbaby` triggers; `.cbBridge` for our `.off()`).
 *  Outbound triggers get `${event}.vidbaby` with the payload wrapped in an
 *  array (jQuery's `trigger(name, extraParams)` requires extraParams to be
 *  an array — passing a bare string makes the handler see undefined).
 *  Empirical: this exact pattern is what Comcast's working adapter uses. */
const LOADER_NAMESPACE = '.vidbaby';
/** Window after mount during which we drop inbound responses. The
 *  dialog flow sometimes fires an auto-opener that we don't want to
 *  forward (scripted lines come through `playResponse`, not the
 *  generic response stream). Pattern lifted from the Comcast live
 *  adapter — empirically a 500ms cutoff handles the common case. */
const AUTO_FIRE_SUPPRESS_MS = 500;
/** Inbound dedupe window. jQuery's namespace semantics mean a single
 *  loader-side `na-response.vidbaby` trigger can sometimes fire our
 *  handler twice within a few ms. We drop the duplicate based on
 *  identical text + tight time delta. */
const RESPONSE_DEDUPE_MS = 80;

interface ResponseEventPayload {
  answerText?: string;
  text?: string;
  /** Some loader builds wrap the response shape under .responseData; we
   *  unwrap before reading answerText. Seen on the DOM CustomEvent path. */
  responseData?: { answerText?: string; text?: string };
}

interface MicrophoneEventPayload {
  text?: string;
  transcript?: string;
  isFinal?: boolean;
  final?: boolean;
}

/** Construct the full loader URL from either:
 *   1. An explicit `loaderUrl` passed in MountOptions (preferred — the
 *      studio writes this verbatim from Bridge3's `avatar_script_tag`).
 *   2. The legacy `${VITE_BRIDGE3_LOADER_URL}?id=<loaderId>` pattern,
 *      for hand-configured or pre-Bridge3-provisioning environments.
 *
 *  startData is appended as a JSON-encoded URL param. Caller must keep
 *  startData under ~1-2 KB (CODEBABY_GUIDE.md §2). */
export function buildLoaderUrl(opts: MountOptions): string {
  const startDataParam = `startData=${encodeURIComponent(JSON.stringify(opts.startData))}`;
  if (opts.loaderUrl && opts.loaderUrl.length > 0) {
    const sep = opts.loaderUrl.includes('?') ? '&' : '?';
    return `${opts.loaderUrl}${sep}${startDataParam}`;
  }
  const base = DEFAULT_LOADER_BASE;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}id=${encodeURIComponent(opts.avatarLoaderId)}&${startDataParam}`;
}

interface MountInternals {
  scriptTag: HTMLScriptElement | null;
  watchdog: number | null;
  /** Once playerBuilt fires, we can dispatch ask/playResponse safely. */
  ready: boolean;
  /** Anything dispatched before playerBuilt is queued and flushed here. */
  pendingDispatch: Array<{ event: string; payload?: unknown }>;
  unmounted: boolean;
  /** Wall-clock at mount — used by auto-fire suppression. */
  mountedAt: number;
  /** Dedupe state for inbound responses. */
  lastResponseSig: { text: string; at: number };
  /** Dedupe state for inbound mic-data events — keyed on (text, isFinal)
   *  per Comcast. The loader fires duplicate interim chunks for the same
   *  STT word as it streams; without dedupe the host gets 3-5 identical
   *  events per word. */
  lastMicSig: { text: string; isFinal: boolean; at: number };
  /** Disconnect handle for the canvas-relocation MutationObserver. */
  relocationDisconnect: (() => void) | null;
  /** Cleanup for the native addEventListener path on window+document.
   *  jQuery .off('.cbBridge') doesn't touch DOM listeners, so we track
   *  these separately and remove them on unmount. */
  domListenersCleanup: (() => void) | null;
}

export function mountAvatarLive(opts: MountOptions): MountResult {
  if (typeof document === 'undefined') {
    throw new Error('mountAvatarLive requires a browser document — invoked in a non-DOM context.');
  }

  // Side effect: ensures opts.containerEl has the canonical id the
  // CodeBaby loader's portal config expects, so the loader auto-mounts
  // into our React-owned container instead of injecting a floating
  // widget overlay. (See Comcast deployment — no explicit buildPlayer
  // dispatch required; the loader finds the container by id.)
  ensureContainerId(opts.containerEl);

  // Container preparation per the Comcast pattern: clear anything our
  // own placeholder card left behind, mark the container for CSS
  // scoping (`.cb-mount`), and tag the mode + loader id as data attrs
  // for in-browser inspection.
  try {
    opts.containerEl.innerHTML = '';
    opts.containerEl.classList.add('cb-mount');
    opts.containerEl.setAttribute('data-cb-mode', 'live');
    opts.containerEl.setAttribute('data-cb-loader', opts.avatarLoaderId);
  } catch {
    // Container is detached or read-only — fail soft; the script tag
    // still gets injected below and any errors surface via onError.
  }

  const url = buildLoaderUrl(opts);

  const state: MountInternals = {
    scriptTag: null,
    watchdog: null,
    ready: false,
    pendingDispatch: [],
    unmounted: false,
    mountedAt: Date.now(),
    lastResponseSig: { text: '', at: 0 },
    lastMicSig: { text: '', isFinal: false, at: 0 },
    relocationDisconnect: null,
    domListenersCleanup: null,
  };

  // Inject the loader script. The loader populates window.vidbaby /
  // window.jQuery once it executes, then auto-mounts into the container
  // whose id matches its portal config (see ensureContainerId above).
  // We don't dispatch `na-buildPlayer` ourselves — the loader fires its
  // own once initialized, and racing it tends to produce double mounts
  // / a floating widget on top of the embedded canvas.
  const scriptTag = document.createElement('script');
  scriptTag.src = url;
  scriptTag.async = true;
  scriptTag.dataset['cbBridge'] = '1';
  scriptTag.onerror = () => {
    if (state.unmounted) return;
    opts.onError?.(
      new Error(
        `CodeBaby loader script failed to load at ${url}. Check: (1) network, ` +
          `(2) the loader URL host is reachable, (3) the avatarLoaderId is valid.`,
      ),
    );
  };
  scriptTag.onload = () => {
    if (state.unmounted) return;
    // The loader needs a tick to populate window.jQuery / window.vidbaby
    // and wire its handlers. Poll briefly, then bind ours.
    // 12-second poll window to match Comcasts working adapter. The loader
    // loads jQuery from the cloudflare CDN asynchronously after our
    // scripts onload fires — empirically that can take 1-3 seconds on
    // a cold cache. A 200ms cap (our previous value) gives up before
    // jQuery is available, attachListeners never runs, and every event
    // the loader fires goes silently uncaught. Once jQuery appears, it
    // does so quickly within one poll interval, so the 12s cap is a
    // long-tail safety net, not a normal-path wait.
    pollForJQueryThen(
      (jq) => {
        if (state.unmounted) return;
        attachListeners(jq);
      },
      12_000,
      100,
    );
    // Independent of the jQuery event path: poll DOM for the canvas
    // appearing. Whichever fires first marks ready + relocates. The
    // loader sometimes fires na-playerBuilt before our jQuery
    // listener is attached, so we can't rely on the event alone.
    pollForCanvasThen(() => markReadyAndRelocate('canvas-poll'));
  };

  function applyVadEagerness(): void {
    const raw = (import.meta.env?.VITE_CB_VAD_EAGERNESS ?? '').toString().trim().toLowerCase();
    if (raw !== 'low' && raw !== 'medium' && raw !== 'high' && raw !== 'auto') return;
    try {
      const vb = window.vidbaby;
      if (!vb) return;
      vb.config = vb.config ?? {};
      vb.config.realtimeVadEagerness = raw;
      console.info(`[cb:live] set realtimeVadEagerness=${raw}`);
    } catch (err) {
      console.warn('[cb:live] could not set VAD eagerness (non-fatal):', err);
    }
  }

  function markReadyAndRelocate(source: 'event' | 'canvas-poll'): void {
    if (state.ready || state.unmounted) return;
    state.ready = true;
    if (state.watchdog !== null) {
      clearTimeout(state.watchdog);
      state.watchdog = null;
    }
    // Move the loader's canvas tree into our React mount. The MLS
    // template mounts at body-level (`.codebaby-ui > … >
    // #babylonPlayerContainer > #playerCanvas`); we relocate
    // #babylonPlayerContainer and trigger a resize so Babylon rebinds.
    // The returned disconnect tears down the MutationObserver that
    // re-relocates when the loader puts the canvas back.
    state.relocationDisconnect = relocateCanvasIntoOurMount(opts.containerEl);

    // Flush anything dispatched before we were ready.
    const jq = currentJQuery();
    if (jq) {
      const $doc = jq(document);
      for (const queued of state.pendingDispatch) {
        $doc.trigger(
          `${queued.event}${LOADER_NAMESPACE}`,
          queued.payload === undefined ? undefined : [queued.payload],
        );
      }
    }
    state.pendingDispatch.length = 0;

    // Override the mic's semantic-VAD eagerness if configured. 'low' makes the
    // VAD wait for clearer speech, cutting the phantom "hello" turns a cough or
    // throat-clear used to trigger. Runtime-settable per the loader; we set it
    // once on ready. Safe no-op if the loader build predates the feature.
    applyVadEagerness();

    opts.onPlayerBuilt?.();
    // Annotate the container so DOM inspection makes clear which path
    // won the race.
    try {
      opts.containerEl.setAttribute('data-cb-ready', source);
    } catch {
      // Container may be detached; non-essential.
    }
  }
  state.scriptTag = scriptTag;
  document.head.appendChild(scriptTag);

  // Watchdog: if playerBuilt doesn't fire within 10s, the loader almost
  // certainly failed silently (bad ID, URL too long, CSP block, jQuery
  // never appeared). Log specifically so the failure mode is obvious.
  state.watchdog = window.setTimeout(() => {
    if (state.unmounted || state.ready) return;
    opts.onError?.(
      new Error(
        `playerBuilt did not fire within ${MOUNT_WATCHDOG_MS}ms — loader likely failed to ` +
          `initialize. Check: (1) the script tag at ${url} actually loaded, ` +
          `(2) startData is under the URL cap, (3) the dialog flow isn't blocked ` +
          `on the CodeBaby admin portal side.`,
      ),
    );
  }, MOUNT_WATCHDOG_MS);

  // ----- Pure handlers — both event paths feed these via synthetic events --
  //
  // The CodeBaby loader fires events via BOTH paths (and the mix varies by
  // event type and loader build):
  //   - jQuery $(target).trigger('na-foo.vidbaby', extraParams)
  //   - native target.dispatchEvent(new CustomEvent('na-foo', {detail}))
  //
  // Comcast Sales Academy's working adapter binds BOTH paths and routes both
  // into the same `(e: Event) => ...` handlers via a relay() that builds a
  // synthetic CustomEvent for the jQuery side. We mirror that here verbatim.
  // Pattern lifted from /Comcast Sales Academy/src/lib/codebaby/live.ts.
  //
  // Each handler reads `e.detail` and accepts both flat (`{answerText}`) and
  // wrapped (`{responseData: {answerText}}`) shapes — both are observed in
  // the wild.

  const onResponseHandler = (e: Event): void => {
    if (state.unmounted) return;
    const detail = (e as CustomEvent).detail as ResponseEventPayload | undefined;
    const data = detail?.responseData ?? detail;
    const text = data?.answerText ?? data?.text ?? '';
    if (text.length === 0) return;

    const now = Date.now();
    if (
      text === state.lastResponseSig.text &&
      now - state.lastResponseSig.at < RESPONSE_DEDUPE_MS
    ) {
      return;
    }
    state.lastResponseSig = { text, at: now };
    if (now - state.mountedAt < AUTO_FIRE_SUPPRESS_MS) {
      console.info(`[cb:live] suppressed response within auto-fire window`, {
        elapsedMs: now - state.mountedAt,
      });
      return;
    }
    console.info(`[cb:live] ← response (${e.type})`, { preview: text.slice(0, 80) });
    const out: ResponsePayload = { text };
    opts.onResponse?.(out);
  };

  const onMicrophoneDataHandler = (e: Event): void => {
    if (state.unmounted) return;
    const detail = (e as CustomEvent).detail;
    const data = (detail?.microphoneData ?? detail) as MicrophoneEventPayload | undefined;
    if (!data) return;
    const text = (data.text ?? data.transcript ?? '').toString();
    if (text.length === 0) return;
    const isFinal = Boolean(data.isFinal ?? data.final);

    // Dedupe — see Comcast adapter. The loader fires duplicate interim
    // chunks for the same STT word (3-5 identical events per word) AND
    // an isFinal:true that often arrives a few ms after the last
    // isFinal:false with identical text. Without dedupe, the host UI
    // sees a flood; the auto-ask trap also triggers when the loader
    // re-fires the final.
    const now = Date.now();
    if (
      text === state.lastMicSig.text &&
      isFinal === state.lastMicSig.isFinal &&
      now - state.lastMicSig.at < RESPONSE_DEDUPE_MS
    ) {
      return;
    }
    state.lastMicSig = { text, isFinal, at: now };

    opts.onMicrophoneData?.({ text, isFinal });
  };

  const onInterimResponseHandler = (e: Event): void => {
    if (state.unmounted) return;
    const detail = (e as CustomEvent).detail;
    const data = (detail?.responseData ?? detail) as ResponseEventPayload | undefined;
    const text = data?.answerText ?? data?.text ?? '';
    if (text.length === 0) return;
    opts.onInterimResponse?.(text);
  };

  const onSegmentEndedHandler = (_e: Event): void => {
    if (state.unmounted) return;
    opts.onSegmentEnded?.();
  };
  const onMicStartedHandler = (_e: Event): void => {
    if (state.unmounted) return;
    opts.onMicrophoneStarted?.();
  };
  const onMicReadyHandler = (_e: Event): void => {
    if (state.unmounted) return;
    opts.onMicrophoneReady?.();
  };
  const onMicStoppedHandler = (_e: Event): void => {
    if (state.unmounted) return;
    opts.onMicrophoneStopped?.();
  };
  const onPlayerBuiltHandler = (_e: Event): void => {
    if (state.unmounted) return;
    markReadyAndRelocate('event');
  };

  /** Inbound events the loader may fire. Each entry is the list of
   *  names we listen on — namespaced (`.vidbaby`) form first for the
   *  jQuery binding, bare form second for the native addEventListener
   *  binding. Listening to both forms is cheap insurance against the
   *  loader build choosing one or the other. Pattern from Comcast. */
  const INBOUND_EVENTS = {
    playerBuilt: ['na-playerBuilt.vidbaby', 'na-playerBuilt'],
    response: ['na-response.vidbaby', 'na-response'],
    // NOTE: na-playResponse is OUTBOUND only (we trigger it for scripted
    // lines via session.playResponse). Comcast's working adapter does NOT
    // bind it as inbound. Empirically the loader ALSO fires
    // na-playResponse alongside na-response for every LLM turn — if we
    // listen for it, the same text reaches the host transcript twice
    // (the 80ms dedupe doesn't catch it because the two events fire ~tens
    // of ms past the window). Linda speaks once but the transcript shows
    // duplicates. Don't add it back.
    interimResponse: ['na-interimResponse.vidbaby', 'na-interimResponse'],
    microphoneData: ['na-microphoneData.vidbaby', 'na-microphoneData'],
    segmentEnded: ['na-segmentEnded.vidbaby', 'na-segmentEnded'],
    microphoneStarted: ['na-microphoneStarted.vidbaby', 'na-microphoneStarted'],
    microphoneReady: ['na-microphoneReady.vidbaby', 'na-microphoneReady'],
    microphoneStopped: ['na-microphoneStopped.vidbaby', 'na-microphoneStopped'],
  } as const;

  /** Pair handler ↔ event names so we can wire jQuery + DOM paths
   *  consistently. Order matches the handler ordering above. */
  const HANDLER_MAP: Array<readonly [readonly string[], (e: Event) => void]> = [
    [INBOUND_EVENTS.playerBuilt, onPlayerBuiltHandler],
    [INBOUND_EVENTS.response, onResponseHandler],
    [INBOUND_EVENTS.interimResponse, onInterimResponseHandler],
    [INBOUND_EVENTS.microphoneData, onMicrophoneDataHandler],
    [INBOUND_EVENTS.segmentEnded, onSegmentEndedHandler],
    [INBOUND_EVENTS.microphoneStarted, onMicStartedHandler],
    [INBOUND_EVENTS.microphoneReady, onMicReadyHandler],
    [INBOUND_EVENTS.microphoneStopped, onMicStoppedHandler],
  ];

  /** Pick the namespaced form for jQuery binding. */
  const NAMESPACED = (names: readonly string[]): string =>
    names.find((n) => n.endsWith('.vidbaby')) ?? names[0]!;

  /** Wrap a jQuery handler invocation into a synthetic CustomEvent so the
   *  same `(e: Event) => void` handler works for both paths. */
  const jqRelay = (handler: (e: Event) => void, name: string) =>
    function (_jqEvent: unknown, ...args: unknown[]): void {
      if (state.unmounted) return;
      let detail: unknown;
      if (args.length === 0) detail = undefined;
      else if (args.length === 1) detail = args[0];
      else detail = args;
      const synthetic = new CustomEvent(name, { detail });
      handler(synthetic);
    };

  // ----- DOM addEventListener path — safety net for native dispatchEvent --
  const domAttachments: Array<[EventTarget, string, EventListener]> = [];
  const attachDom = (
    targets: EventTarget[],
    names: readonly string[],
    handler: EventListener,
  ): void => {
    for (const target of targets) {
      for (const name of names) {
        target.addEventListener(name, handler, true);
        domAttachments.push([target, name, handler]);
      }
    }
  };
  const domTargets: EventTarget[] = [window, document];
  for (const [names, handler] of HANDLER_MAP) {
    attachDom(domTargets, names, handler as EventListener);
  }
  state.domListenersCleanup = () => {
    for (const [target, name, handler] of domAttachments) {
      target.removeEventListener(name, handler, true);
    }
    domAttachments.length = 0;
  };

  function attachListeners(jq: JQueryLike): void {
    const $doc = jq(document);

    // ----- jQuery .on() path — primary path for loader's $.trigger() ------
    //
    // Listener namespace is `.vidbaby.cbBridge`:
    //   - `.vidbaby` matches the loader's outbound trigger namespace.
    //   - `.cbBridge` lets us tear down all of our bindings with a single
    //     `.off('.cbBridge')` at unmount.
    //
    // We bind the namespaced form only (e.g. `na-response.vidbaby.cbBridge`).
    // Per jQuery's namespace semantics, a listener bound with `.vidbaby`
    // also fires when the trigger has no namespace (or has the `.vidbaby`
    // namespace). Binding the bare form too would double-fire every event.
    for (const [names, handler] of HANDLER_MAP) {
      const nameWithNs = NAMESPACED(names);
      $doc.on(`${nameWithNs}${HANDLER_NAMESPACE}`, jqRelay(handler, nameWithNs));
    }

    // ----- Wildcard sniffer — production diagnostic ----------------------
    //
    // Logs every `.vidbaby` event the loader fires so we can SEE what's
    // happening when something doesn't reach a typed handler above. This
    // is left on in production deliberately — when a regression shows up
    // we want the console to tell us what fired without redeploying.
    // Pattern from Comcast Sales Academy.
    $doc.on(`.vidbaby${HANDLER_NAMESPACE}`, (...args: unknown[]) => {
      if (state.unmounted) return;
      const jqEvent = args[0] as { type?: string; namespace?: string } | undefined;
      console.info(`[cb:live] ⚡ ${jqEvent?.type ?? '?'}.${jqEvent?.namespace ?? ''}`, {
        extra: args.slice(1),
      });
    });
  }

  function dispatch(event: string, payload?: unknown): void {
    if (state.unmounted) return;
    const jq = currentJQuery();
    if (!jq || !state.ready) {
      state.pendingDispatch.push({ event, payload });
      return;
    }
    // Trigger with `.vidbaby` namespace so the loader's `na-*.vidbaby`
    // listeners actually fire — and wrap the payload in an array, which is
    // how jQuery passes extraParams into the listener.
    jq(document).trigger(
      `${event}${LOADER_NAMESPACE}`,
      payload === undefined ? undefined : [payload],
    );
  }

  const session: AvatarSession = {
    ask(text: string) {
      // Typed input only — DO NOT call this from onMicrophoneData with
      // isFinal=true. (Auto-ask trap.)
      dispatch('na-ask', text);
    },
    playResponse(text: string) {
      // System-owned scripted lines. Dialog flow on the portal side
      // must be configured to stay silent on mount for the mode the
      // host uses this from (debrief / lesson / playbook).
      dispatch('na-playResponse', { answer: text });
    },
    toggleMicrophone() {
      dispatch('na-microphoneToggle');
    },
    mute() {
      dispatch('na-mute');
    },
    unmute() {
      dispatch('na-unmute');
    },
    resetConversation() {
      dispatch('na-resetConversation');
    },
    pushContext(context) {
      // The runtime-context channel — bypasses the URL cap.
      dispatch('na-conversationalContextData', context);
    },
    unmount() {
      if (state.unmounted) return;
      state.unmounted = true;
      if (state.watchdog !== null) {
        clearTimeout(state.watchdog);
        state.watchdog = null;
      }
      if (state.relocationDisconnect !== null) {
        state.relocationDisconnect();
        state.relocationDisconnect = null;
      }
      if (state.domListenersCleanup !== null) {
        state.domListenersCleanup();
        state.domListenersCleanup = null;
      }
      const jq = currentJQuery();
      if (jq) {
        // One namespaced .off detaches every handler we attached.
        jq(document).off(HANDLER_NAMESPACE);
      }
      if (state.scriptTag && state.scriptTag.parentNode) {
        state.scriptTag.parentNode.removeChild(state.scriptTag);
      }
      state.scriptTag = null;
      // Wipe container innerHTML — but leave window.vidbaby /
      // window.jQuery intact (subsequent mounts depend on them).
      try {
        opts.containerEl.innerHTML = '';
      } catch {
        // Container might already be detached; nothing to clean.
      }
    },
  };

  return { session };
}

/** Resolve a jQuery surface from window, in priority order:
 *    1. `window.vidbaby$` — the loader's own namespaced jQuery (a top-level
 *       callable, NOT `window.vidbaby.$`). Critical: this is the jQuery
 *       instance the loader's own listeners are bound to. If we use a
 *       different instance, our `.trigger()` calls fire to a different
 *       internal event registry and the loader never hears them.
 *    2. `window.vidbaby.$` — older loader builds expose jQuery as a
 *       property of the vidbaby object instead.
 *    3. `window.jQuery` / `window.$` — page-level jQuery (last-resort
 *       fallback; may be the same instance as the loader's, or may not).
 *  Only return a candidate whose typeof is 'function' — vidbaby exposes
 *  non-callable objects on similar names that would throw on invocation. */
function currentJQuery(): JQueryLike | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as Record<string, unknown>;
  const candidates: unknown[] = [
    w['vidbaby$'],
    (w['vidbaby'] as { $?: unknown } | undefined)?.$,
    w['jQuery'],
    w['$'],
  ];
  for (const c of candidates) {
    if (typeof c === 'function') return c as JQueryLike;
  }
  return null;
}

function pollForJQueryThen(
  onReady: (jq: JQueryLike) => void,
  totalMs: number,
  intervalMs: number,
): void {
  const start = Date.now();
  let tickCount = 0;
  const tick = (): void => {
    tickCount += 1;
    const jq = currentJQuery();
    // Diagnostic dump — prints what's at every candidate window slot on
    // every poll tick. Once jQuery is found we stop polling and stop
    // logging. console.warn is used so it's visible even with default
    // console levels (Info gets filtered out in some configs).
    const w = window as unknown as Record<string, unknown>;
    const vid = w['vidbaby'] as { $?: unknown } | undefined;
    console.warn(`[cb:poll] tick=${tickCount} jq=${jq ? 'FOUND' : 'null'}`, {
      'window.vidbaby$': typeof w['vidbaby$'],
      'window.vidbaby': typeof w['vidbaby'],
      'window.vidbaby.$': typeof vid?.$,
      'window.jQuery': typeof w['jQuery'],
      'window.$': typeof w['$'],
      vidbabyKeys: vid && typeof vid === 'object' ? Object.keys(vid).slice(0, 10) : null,
    });
    if (jq) {
      console.warn(`[cb:poll] ✓ attaching listeners — jQuery found after ${tickCount} ticks`);
      onReady(jq);
      return;
    }
    if (Date.now() - start >= totalMs) {
      console.warn(`[cb:poll] ✗ GAVE UP after ${tickCount} ticks / ${Date.now() - start}ms`);
      return;
    }
    setTimeout(tick, intervalMs);
  };
  tick();
}

/** Poll for `#babylonPlayerContainer` (the canvas's DOM parent) to
 *  appear. Independent of the jQuery `na-playerBuilt` event because
 *  that one sometimes fires before our handler attaches. Times out at
 *  the same horizon as the playerBuilt watchdog. */
function pollForCanvasThen(onReady: () => void): void {
  if (typeof document === 'undefined') return;
  const start = Date.now();
  const tick = (): void => {
    const el = document.getElementById('babylonPlayerContainer');
    if (el) {
      onReady();
      return;
    }
    if (Date.now() - start >= 12_000) {
      return;
    }
    setTimeout(tick, 100);
  };
  tick();
}

/** Container id the CodeBaby loader looks for. The loader's portal
 *  config for each avatar specifies a target container id; the
 *  cross-tenant convention (validated against the Comcast deployment)
 *  is `avatarContainer`. Caller can override by setting the id on the
 *  container element before mount. */
const DEFAULT_CONTAINER_ID = 'avatarContainer';

function ensureContainerId(el: HTMLElement): string {
  if (el.id && el.id.length > 0) return `#${cssEscape(el.id)}`;
  el.id = DEFAULT_CONTAINER_ID;
  return `#${DEFAULT_CONTAINER_ID}`;
}

/** Minimal CSS.escape for the id we generate / accept. Browsers ship
 *  CSS.escape but it's not in jsdom by default and we want this to
 *  remain test-friendly. */
function cssEscape(id: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(id);
  }
  return id.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}

/** Move `#babylonPlayerContainer` (the Bridge3 MLS template's canvas
 *  parent) into our React-owned mount, then dispatch a window resize
 *  so Babylon rebinds its rendertarget to the new parent's size.
 *
 *  The Bridge3 MLS template + Abby_Avatar_Script appears to put the
 *  canvas back after a one-shot relocation (empirically observed
 *  2026-05-26 — our `data-cb-ready` got set but the canvas was still
 *  inside `.codebaby-ui` at body level). So in addition to the
 *  initial move, we install a MutationObserver that re-relocates
 *  whenever the DOM mutates and the canvas is anywhere other than
 *  our target. The observer auto-disconnects after a 15s budget or
 *  6 relocations, whichever comes first — if the loader is still
 *  fighting us after that, something else is going on and we want
 *  to surface it rather than spin forever.
 *
 *  Also kills the floating "Talk to <Name>" pill that
 *  Abby_Avatar_Script3_1_1.js injects — its class varies, so we
 *  identify it by text content + fixed positioning. Runs on the
 *  same observer cadence.
 *
 *  Returns a disconnect function the caller wires into unmount.
 *
 *  Exported for tests. */
export function relocateCanvasIntoOurMount(target: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => undefined;

  let relocationCount = 0;
  const MAX_RELOCATIONS = 6;
  const OBSERVER_WINDOW_MS = 15_000;

  function tryRelocate(): boolean {
    if (typeof document === 'undefined') return false;
    const playerContainer = document.getElementById('babylonPlayerContainer');
    if (!playerContainer) return false;

    // On the /avatars baseUi, babylonPlayerContainer is slot-projected into a
    // custom element (avatar-container-XXXX, shadow DOM, <slot name="avatar">).
    // We can't move that wrapper or its slotted child without triggering the
    // loader's connectedCallback → relocateAvatar (boilerplate.js:140), which
    // throws on a null parent reference. Instead, steal just the <canvas>:
    // it's a plain leaf node with no custom-element machinery, the WebGL
    // context survives reparenting, and the loader's internal book-keeping
    // (which references babylonPlayerContainer, not the canvas) stays intact.
    // Legacy /mls/listings deployments also have a canvas inside
    // babylonPlayerContainer, so this path covers both.
    const canvas = playerContainer.querySelector('canvas');
    if (!canvas) return false;
    if (canvas.parentElement === target) return false;

    try {
      target.appendChild(canvas);
    } catch {
      // Defensive: if the loader throws during the reparent, the canvas may
      // still have been appended (DOM commit-once). Continue.
    }
    try {
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
    } catch {
      // Skip.
    }
    relocationCount += 1;
    if (typeof window !== 'undefined') {
      // Tell Babylon to rebind to the new parent's size.
      window.dispatchEvent(new Event('resize'));
    }
    return true;
  }

  // First-pass: poll briefly via rAF in case the player container
  // hasn't been attached yet.
  let attempts = 0;
  const tick = (): void => {
    attempts += 1;
    tryRelocate();
    const exists = document.getElementById('babylonPlayerContainer');
    if (!exists && attempts < 30 && typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(tick);
    }
  };
  tick();

  removeFloatingVoiceButton();

  // Observer: re-relocate on any DOM mutation that brings the canvas
  // back under .codebaby-ui (or anywhere outside our target). The
  // template's Abby_Avatar_Script and the loader's responsive
  // breakpoint logic both poke at the canvas's parent after our
  // initial move.
  let observer: MutationObserver | null = null;
  let disconnectTimer: number | null = null;

  if (typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(() => {
      if (relocationCount >= MAX_RELOCATIONS) {
        observer?.disconnect();
        observer = null;
        return;
      }
      tryRelocate();
      removeFloatingVoiceButton();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    if (typeof window !== 'undefined') {
      disconnectTimer = window.setTimeout(() => {
        observer?.disconnect();
        observer = null;
      }, OBSERVER_WINDOW_MS);
    }
  }

  return () => {
    if (disconnectTimer !== null && typeof window !== 'undefined') {
      window.clearTimeout(disconnectTimer);
    }
    observer?.disconnect();
    observer = null;
  };
}

/** Find and remove the "Talk to <Name>" floating pill the template's
 *  `Abby_Avatar_Script` injects. Selector-free — identifies by visible
 *  text content + fixed/absolute positioning. Runs a short polling
 *  loop because the script may re-create it if we miss the first
 *  injection. */
function removeFloatingVoiceButton(): void {
  if (typeof document === 'undefined') return;
  // "Talk to <name>" is distinctive enough that we don't need to
  // match the optional mic emoji prefix that Abby_Avatar_Script
  // sometimes injects. Keeping the pattern ASCII-only avoids
  // eslint's no-misleading-character-class warnings for
  // surrogate-pair emojis + variation selectors.
  const TEXT_PATTERN = /^\s*Talk to /i;
  const MAX_ATTEMPTS = 50;
  let attempts = 0;
  const tick = (): void => {
    attempts += 1;
    let removed = 0;
    const candidates = document.body.querySelectorAll<HTMLElement>('button, div, a, span');
    for (const el of candidates) {
      const text = (el.textContent ?? '').trim();
      if (!TEXT_PATTERN.test(text)) continue;
      const cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'absolute') continue;
      el.remove();
      removed += 1;
    }
    if (attempts < MAX_ATTEMPTS && removed === 0) {
      setTimeout(tick, 200);
    }
  };
  tick();
}
